<template>
  <div class="resume-container">
    <div class="personal-info-container">
      <div class="photo-container">
        <img class="photo" :src="$DjangoMediaUrl(resumeData.photo)" @error.once="getDefaultProfilePhoto($event)">
      </div>
      <div class="personal-info">
        <span class="name">{{ resumeData.name }}</span>
        <p class="personal-statement">{{ resumeData.personal_statement }}</p>
      </div>
    </div>
    <div v-if="aiSummaryInfo" class="header-container ai-summary-info">
      <span class="title">{{ $t('hm.resume.aiSummaryInfo') }}</span>
    </div>
    <div v-if="aiSummaryInfo" class="info-container line-item">
      <div class="card-line">{{ aiSummaryInfo }}</div>
    </div>

    <div v-if="aiMatchScore" class="header-container ai-match-info">
      <div class="card-title">
        <span class="title">{{ $t('hm.resume.aiResumeMatchInfo') }}</span>
        <div class="btn-container">
          <div class="score-container">
            <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="$t('admin.application.candidate.tip.resumeFitInfo')">
              <div ref="aiMatchFitScore" class="chart-score"></div>
            </el-tooltip>
          </div>
        </div>
      </div>
    </div>
    <div v-if="aiMatchScore" class="info-container line-item">
      <div class="card-line">{{ aiMatchSummary }}</div>
    </div>
    <div class="header-container basic-info">
      <span class="title">{{ $t('hm.resume.basicInfo') }}</span>
      <div class="download-resume" v-if="resumeData.resume_path">
        <a href="javascript:void(0)" @click="handleDownloadOriginalResume">
          <i class="iconfont icondownload"></i>
          {{ $t('hm.resume.originalResume') }}
        </a>
      </div>
    </div>
    <div class="info-container basic">
      <div class="apply-for" v-if="resumeData.job">
        <span class="title">{{ $t('hm.resume.applyFor') }}</span>
        <span class="content">{{ resumeData.job.title }}</span>
      </div>
      <div class="apply-date">
        <span class="title">{{ $t('hm.resume.jobApplicationDate') }}</span>
        <span class="content">{{ formatTime(resumeData.apply_time) }}</span>
      </div>
      <div class="candidate-name" v-if="resumeData.candidate">
        <span class="title">{{ $t('hm.resume.legalName') }}</span>
        <span class="content">{{ resumeData.candidate.name }}</span>
      </div>
      <div class="candidate-email">
        <span class="title">{{ $t('hm.resume.emailAddress') }}</span>
        <span class="content">{{ resumeData.email }}</span>
      </div>
      <div class="candidate-mobile" v-if="resumeData.mobile_id">
        <span class="title">{{ $t('hm.resume.mobileId') }}</span>
        <span class="content">{{ resumeData.mobile_id }}</span>
      </div>
      <div class="candidate-stlutation" v-if="resumeData.salutation">
        <span class="title">{{ $t('hm.resume.stlutation') }}</span>
        <span class="content">{{ resumeData.salutation }}</span>
      </div>
      <div class="candidate-birthday" v-if="resumeData.birthday">
        <span class="title">{{ $t('hm.resume.birthday') }}</span>
        <span class="content">{{ formatTime(resumeData.birthday) }}</span>
      </div>
      <div class="candidate-gender" v-if="resumeData.gender">
        <span class="title">{{ $t('hm.resume.gender') }}</span>
        <span class="content">{{ resumeData.gender }}</span>
      </div>
      <div class="candidate-location" v-if="resumeData.hukou_location">
        <span class="title">{{ $t('hm.resume.location') }}</span>
        <span class="content">{{ resumeData.hukou_location }}</span>
      </div>
      <div class="candidate-current-location" v-if="resumeData.current_location">
        <span class="title">{{ $t('hm.resume.currentLocation') }}</span>
        <span class="content">{{ resumeData.current_location }}</span>
      </div>
      <div class="candidate-address" v-if="resumeData.address1">
        <span class="title">{{ $t('hm.resume.address') }}</span>
        <span class="content">{{ resumeData.address1 }}</span>
      </div>
      <div class="candidate-address-other" v-if="resumeData.address2">
        <span class="title">{{ $t('hm.resume.addressOther') }}</span>
        <span class="content">{{ resumeData.address2 }}</span>
      </div>

      <div class="candidate-mobile-phone" v-if="resumeData.mobile">
        <span class="title">{{ $t('hm.resume.mobilePhone') }}</span>
        <span class="content">{{ resumeData.mobile }}</span>
      </div>
      <div class="candidate-second-phone" v-if="resumeData.second_phone">
        <span class="title">{{ $t('hm.resume.secondPhone') }}</span>
        <span class="content">{{ resumeData.second_phone }}</span>
      </div>
      <div class="candidate-chat-id" v-if="resumeData.chat_tools && resumeData.chat_tools.length > 0">
        <span class="title">{{ $t('hm.resume.chatId') }}</span>
        <div class="list-container  chat-tools">
          <div
            v-for="(item, index) in resumeData.chat_tools"
            :key="'chat_tools-' + index">
            <span
              class="name"
              >{{ item.tool_name }}</span>
              <span class="value">{{ item.value }}</span>
          </div>
        </div>
      </div>
      <div class="candidate-social-network" v-if="resumeData.social_net_works && resumeData.social_net_works.length > 0">
        <span class="title">{{ $t('hm.resume.socialNetwork') }}</span>
        <div class="list-container social-network">
          <div
            v-for="(item, index) in resumeData.social_net_works"
            :key="'social_net_works-' + index">
            <span
              class="name"
              >{{ item.net_work_name }}</span>
              <span class="value">{{ item.value }}</span>
          </div>
        </div>
      </div>
      <div class="candidate-expected-salary" v-if="resumeData.expected_salary">
        <span class="title">{{ $t('hm.resume.expectedSalary') }}</span>
        <span class="content">{{ resumeData.expected_salary }}</span>
      </div>
    </div>
    <div class="referral-info-container" v-if="resumeData.referral_info && resumeData.referral_info.email">
      <div class="info">
        <div class="title">{{ $t('hm.resume.referredBy') }}</div>
        <div class="info-box">
          <span class="referral-title">
            <span class="iconfont iconidentity"></span>
          </span>
          <span class="text">{{ (resumeData.referral_info.employee && resumeData.referral_info.employee.status) ? $t('hm.resume.employee') : $t('hm.resume.notEmployee') }}</span>
        </div>
        <div class="info-box" v-if="resumeData.referral_info.mobile">
          <span class="referral-title">
            <span class="iconfont iconphone"></span>
          </span>
          <span class="text">{{ resumeData.referral_info.mobile }}</span>
        </div>
        <div class="info-box" v-if="resumeData.referral_info.email">
          <span class="referral-title">
            <span class="iconfont iconemail"></span>
          </span>
          <span class="text">{{ resumeData.referral_info.email }}</span>
        </div>
        <div class="info-box" v-if="resumeData.referral_info.name">
          <span class="referral-title">
            <span class="iconfont iconcandidate"></span>
          </span>
          <span class="text">{{ resumeData.referral_info.name }}</span>
        </div>
        <div class="info-box referral" v-if="resumeData.referral_info.referral_reason">
          <span class="title referral-title">{{ $t('hm.resume.referralReson') }}</span>
          <span class="text">{{ resumeData.referral_info.referral_reason }}</span>
        </div>
      </div>
      <div class="chain">
        <ReferralChain :referralChainInfo="resumeData.referral_chain" :isPc="true"/>
      </div>
    </div>

    <div class="header-container experience" v-show="showExperience">
      <span class="title">{{ $t('hm.resume.experience') }}</span>
    </div>

    <div
      class="info-container experience"
      v-for="(item, index) in resumeData.experience"
      :key="'experience' + index">
      <div class="company" v-if='item.company_name'>
        <span class="title">{{ $t('hm.resume.workExperience.company') }}</span>
        <span class="content">{{ item.company_name }}</span>
      </div>
      <div class="job-title" v-if='item.job_title'>
        <span class="title">{{ $t('hm.resume.workExperience.jobTitle') }}</span>
        <span class="content">{{ item.job_title }}</span>
      </div>
      <div class="industry" v-if='item.industry'>
        <span class="title">{{ $t('hm.resume.workExperience.industry') }}</span>
        <span class="content">{{ item.industry }}</span>
      </div>
      <div class="location" v-if='item.location'>
        <span class="title">{{ $t('hm.resume.workExperience.locaton') }}</span>
        <span class="content">{{ item.location }}</span>
      </div>
      <div class="date">
        <span class="title" v-if='item.start_date'>{{ $t('hm.resume.workExperience.date') }}</span>
        <span class="content">{{ formatTimeArea(item.start_date, item.end_date) }}</span>
      </div>
      <div class="salary" v-if='item.salary'>
        <span class="title">{{ $t('hm.resume.workExperience.salary') }}</span>
        <span class="content">{{ item.salary }}</span>
      </div>
      <div class="description" v-if='item.description'>
        <span class="title">{{ $t('hm.resume.workExperience.description') }}</span>
        <span class="content">{{ item.description }}</span>
      </div>
    </div>

    <div class="header-container intern-experience" v-show="showInternExperience">
      <span class="title">{{ $t('hm.resume.intern') }}</span>
    </div>

    <div
      class="info-container intern-experience"
      v-for="(item, index) in resumeData.intern_experience"
      :key="'intern_experience-' + index">
      <div class="company" v-if='item.company_name'>
        <span class="title">{{ $t('hm.resume.workExperience.company') }}</span>
        <span class="content">{{ item.company_name }}</span>
      </div>
      <div class="job-title" v-if='item.job_title'>
        <span class="title">{{ $t('hm.resume.workExperience.jobTitle') }}</span>
        <span class="content">{{ item.job_title }}</span>
      </div>
      <div class="industry" v-if='item.industry'>
        <span class="title">{{ $t('hm.resume.workExperience.industry') }}</span>
        <span class="content">{{ item.industry }}</span>
      </div>
      <div class="location" v-if='item.location'>
        <span class="title">{{ $t('hm.resume.workExperience.locaton') }}</span>
        <span class="content">{{ item.location }}</span>
      </div>
      <div class="date" v-if='item.start_date'>
        <span class="title">{{ $t('hm.resume.workExperience.date') }}</span>
        <span class="content">{{ formatTimeArea(item.start_date, item.end_date) }}</span>
      </div>
      <div class="salary" v-if='item.salary'>
        <span class="title">{{ $t('hm.resume.workExperience.salary') }}</span>
        <span class="content">{{ item.salary }}</span>
      </div>
      <div class="description" v-if='item.description'>
        <span class="title">{{ $t('hm.resume.workExperience.description') }}</span>
        <span class="content">{{ item.description }}</span>
      </div>
    </div>

    <div class="header-container education" v-show="showEducation">
      <span class="title">{{ $t('hm.resume.education') }}</span>
    </div>

    <div
      class="info-container education"
      v-for="(item, index) in resumeData.education"
      :key="'education-' + index">
      <div class="school" v-if="item.school_name">
        <span class="title">{{ $t('hm.resume.educationInfoLabel.school') }}</span>
        <span class="content">{{ item.school_name }}</span>
      </div>
      <div class="degree" v-if="item.degree">
        <span class="title">{{ $t('hm.resume.educationInfoLabel.degree') }}</span>
        <span class="content">{{ item.degree }}</span>
      </div>
      <div class="date" v-if="item.start_date">
        <span class="title">{{ $t('hm.resume.educationInfoLabel.date') }}</span>
        <span class="content">{{ formatTimeArea(item.start_date, item.end_date) }}</span>
      </div>
      <div class="gpa" v-if="item.gpa">
        <span class="title">{{ $t('hm.resume.educationInfoLabel.gpa') }}</span>
        <span class="content">{{ item.gpa }}</span>
      </div>
      <div class="academic" v-if="item.academic_rating">
        <span class="title">{{ $t('hm.resume.educationInfoLabel.academic') }}</span>
        <span class="content">{{ formatAcademicRating(item.academic_rating) }}</span>
      </div>
      <div class="summary" v-if="item.summary">
        <span class="title">{{ $t('hm.resume.educationInfoLabel.summary') }}</span>
        <span class="content">{{ item.summary }}</span>
      </div>
    </div>

    <div class="header-container additional-info" v-show="showAdditionalInfo">
      <span class="title">{{ $t('hm.resume.additionalInfo') }}</span>
    </div>

    <div
      class="info-container additional-info"
      v-if="resumeData.additional_info"
      >
      <!-- <div class="memberships" v-if="resumeData.additional_info.professional_member">
        <span class="title">{{ $t('hm.resume.additionInfoLabel.memberships') }}</span>
        <span class="content">{{ resumeData.additional_info.professional_member }}</span>
      </div>
      <div class="awards" v-if="resumeData.additional_info.awards">
        <span class="title">{{ $t('hm.resume.additionInfoLabel.awards') }}</span>
        <span class="content">{{ resumeData.additional_info.awards }}</span>
      </div>
      <div class="hobby" v-if="resumeData.additional_info.hobbies">
        <span class="title">{{ $t('hm.resume.additionInfoLabel.hobby') }}</span>
        <span class="content">{{ resumeData.additional_info.hobbies }}</span>
      </div> -->
      <div class="lanaguages" v-if="resumeData.additional_info.languages && resumeData.additional_info.languages.length > 0">
        <span class="title">{{ $t('hm.resume.additionInfoLabel.lanaguages') }}</span>
        <div class="list-container lanaguages content">
          <div
            v-for="(item, index) in resumeData.additional_info.languages"
            :key="'languages-' + index">
              {{ item }}
          </div>
        </div>
      </div>
      <div class="channel" v-if="resumeData.additional_info.channel">
        <span class="title">{{ $t('hm.resume.additionInfoLabel.channel') }}</span>
        <span class="content">{{ resumeData.additional_info.channel }}</span>
      </div>
      <div class="platform" v-if="resumeData.additional_info.platform">
        <span class="title">{{ $t('hm.resume.additionInfoLabel.platform') }}</span>
        <span class="content">{{ resumeData.additional_info.platform }}</span>
      </div>
    </div>

    <HMRateInfo v-if="showHMRateInfo" :hmRatingInfo="resumeData.hmRatingInfo"/>

    <Questionnaire
      v-if="resumeData.standard_questionnaire && resumeData.standard_questionnaire.length > 0"
      :questionnaireTitle="$t('hm.resume.applicantQuestionnaire')"
      :questionnaireList="resumeData.standard_questionnaire"
      @videoPop="handelVideoPop"/>

    <Questionnaire
      v-if="resumeData.hr_survey && resumeData.hr_survey.length > 0"
      class="questionnaire hr-questionnaire"
      :questionnaireList="resumeData.hr_survey"
      :questionnaireTitle="$t('hm.resume.hrQuestionnaire')"
      @videoPop="handelVideoPop"/>

    <Questionnaire
      v-if="resumeData.invite_questionnaire && resumeData.invite_questionnaire.length > 0"
      class="questionnaire specific-questions"
      :questionnaireList="resumeData.invite_questionnaire"
      :questionnaireTitle="$t('hm.resume.specificQuestions')"
      @videoPop="handelVideoPop"/>

  </div>
</template>

<script>
import * as echarts from 'echarts'
import ReferralChain from '../../common/components/referralChain.vue'
import Questionnaire from './questionnaire.vue'
import HMRateInfo from './hmRatingInfo.vue'
import moment from 'moment'
import { getI18nPropertyValue, isEn } from '@/i18n'
import { generateFitEchartsOption } from '@/constant/application'
import { getJobFitScoreColor, formatJobFitScore } from '@/utils/common'
import { getFileExtension } from '@/utils/oss'
import hmApi from '@/api/hm'

export default {
  components: {
    ReferralChain,
    Questionnaire,
    HMRateInfo
  },
  props: {
    resumeData: {
      require: true
    },
    jobId: [String, Number],
    candidateId: [String, Number]
  },
  data () {
    return {
      textSize: {
        minRows: 3,
        maxRows: 5
      },
      activeNames: []
    }
  },
  computed: {
    attachment () {
      return '//cdn.ajinga.com/ajinga-img/attachment_download.png'
    },
    showInternExperience () {
      return this.resumeData?.intern_experience?.length > 0
    },
    showExperience () {
      return this.resumeData?.experience?.length > 0
    },
    showEducation () {
      return this.resumeData?.education?.length > 0
    },
    showAdditionalInfo () {
      return this.resumeData?.additional_info
    },
    showHMRateInfo () {
      const rateList = this.resumeData?.hmRatingInfo?.rate_list
      if (rateList && rateList.length > 0) {
        return true
      }
      return false
    },
    aiSummaryInfo () {
      if (!this.resumeData.ai_summary_info) {
        return ''
      }
      return getI18nPropertyValue(this.resumeData.ai_summary_info, 'ai_summary_cn', 'ai_summary_en')
    },
    aiMatchScore () {
      if (!this.resumeData.ai_match_info) {
        return
      }
      return this.resumeData.ai_match_info.resume_ai_match_score
    },
    aiMatchSummary () {
      if (!this.resumeData.ai_match_info) {
        return ''
      }
      return getI18nPropertyValue(this.resumeData.ai_match_info, 'resume_ai_match_summary_cn', 'resume_ai_match_summary_en')
    }
  },
  created () {
  },
  mounted () {
    this.initFitScore()
  },
  beforeDestroy () {

  },
  methods: {
    async handleDownloadOriginalResume (event) {
      event.preventDefault()
      const extension = getFileExtension(this.resumeData.resume_path)
      const baseName = this.resumeData.name || this.resumeData.candidate?.name || 'resume'
      const fileName = extension ? `${baseName}.${extension}` : baseName
      try {
        const result = await hmApi.getResumeDownloadUrl(this.jobId, this.candidateId, { file_name: fileName })
        window.location.href = result.url
      } catch (error) {
        this.$message.error(this.$t('common.message.downloadFailed'))
      }
    },
    initFitScore () {
      if (!this.aiMatchScore) {
        return
      }
      const el = this.$refs.aiMatchFitScore
      const val = this.aiMatchScore
      const color = getJobFitScoreColor(val)
      const displayValue = formatJobFitScore(val)
      const option = generateFitEchartsOption(val, displayValue, color, 14)
      const series = option.series
      series.axisLine.lineStyle.width = 2
      const chart = echarts.init(el)
      chart.setOption(option)
      this.$nextTick(() => {
        chart.resize()
      })
    },
    handelVideoPop (item) {
      this.$emit('videoPop', item)
    },
    getDefaultProfilePhoto (e) {
      e.target.src = this.$DjangoStaticUrl('images_en/default.jpg')
    },
    formatTime (time) {
      if (time) {
        const date = moment(time)
        return date.format('MM/DD/YYYY')
      }
      return ''
    },
    formatWorkTime (startTime, endTime) {
      if (!startTime) {
        return ''
      }
      const startDate = moment(startTime)
      startDate.locale(isEn() ? 'en' : 'zh-cn')
      let endDate = moment(new Date())
      endDate.locale(isEn() ? 'en' : 'zh-cn')
      if (endTime) {
        endDate = moment(endTime)
      }
      const months = endDate.diff(startDate, 'months')
      const month = months % 12
      const year = (months - month) / 12
      let label = ''
      if (year) {
        label += this.$t('hm.resume.workExperience.year', { year: year })
      }
      if (month) {
        label += ' ' + this.$t('hm.resume.workExperience.month', { month: month })
      }
      return startDate.format('MMMM YYYY') + ' - ' + endDate.format('MMMM YYYY') + ` (${label})`
    },
    formatTimeArea (startTime, endTime) {
      if (!startTime) {
        return ''
      }
      const startDate = moment(startTime)
      let endDate = moment(new Date())
      if (endTime) {
        endDate = moment(endTime)
      }
      return startDate.format('MM/YYYY') + ' - ' + endDate.format('MM/YYYY')
    },
    formatAcademicRating (academicRating) {
      if (academicRating === 40 || academicRating === '40') {
        return this.$t('hm.resume.educationInfoLabel.other')
      }
      return `Top ${academicRating}%`
    }
  },
  watch: {
    aiMatchScore: {
      handler () {
        this.$nextTick(() => {
          this.initFitScore()
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.resume-container {
  .personal-info-container {
    display: flex;
    padding: 30px 40px;
    background-color: #FFFFFF;

    .photo-container {

      .photo {
        width: 100px;
      }
    }

    .personal-info {
      margin-left: 15px;
      padding: 0 10px;

      .name {
        font-size: 20px;
        font-weight: 600;
        line-height: 1;
      }

      .personal-statement {
        margin: 10px 0;
        padding: 10px 0;
        white-space: pre-line;
        max-height: 120px;
        overflow: auto;
      }
    }
  }

  .header-container {
    display: flex;
    justify-content: space-between;
    padding: 10px 0 5px 10px;
    border-bottom: 1px solid #b4b4b4;

    .title {
      font-size: 20px;
      font-weight: 600;
    }

    .download-resume {
      color: #FFFFFF;
      padding: 5px 20px;
      background-color: #0096cc;
      border-radius: 2px;

      > a {
        color: #FFFFFF;

        > i {
          margin-right: 5px;
        }
      }
    }
  }

  .info-container {
    background-color: #FFFFFF;
    margin-top: 20px;
    padding: 30px 10px;

    &> div {
      margin: 10px 0;
      display: flex;
      align-items: center;

      &> span {
        padding-left: 20px;
        width: 65%;
        font-size: 18px;
      }

      .title {
          width: 30%;
          padding: 0;
          text-align: right;
      }
    }

    .candidate-chat-id {
      font-size: 18px;
      align-items: flex-start;
    }

    .candidate-social-network {
      font-size: 18px;
      align-items: flex-start;
    }

    .list-container {
      margin-left: 20px;
      width: 40%;

      &> div {
        font-size: 18px;
        display: flex;
        margin-bottom: 5px;
        justify-content: space-between;
      }
    }

    &.additional-info {
      .lanaguages {
        align-items: flex-start;
      }
    }
  }

  .ai-match-info {
    .card-title {
      position: relative;
      .btn-container {
        position: absolute;
        left: 152px;
        top: 50%;
        transform: translateY(-50%);
        .score-container {
          position: relative;
          .chart-score {
            display: inline-block;
            vertical-align: top;
            width: 28px;
            height: 28px;
          }
        }
      }
    }
  }

  .line-item {
    .card-line {
      margin: 10px 0 0 152px;
      word-break: break-word;
      white-space: pre-wrap;
    }
  }

  .experience {
    .description {
      align-items: flex-start;
      .content {
        white-space: pre-line;
      }
    }
  }
  .intern-experience {
    .description {
      align-items: flex-start;
      .content {
        white-space: pre-line;
      }
    }
  }

  .additional-info {
    .memberships {
      align-items: flex-start;
      .content {
        white-space: pre-line;
      }
    }

    .hobby {
      align-items: flex-start;
      .content {
        white-space: pre-line;
      }
    }
  }

  .referral-info-container {
    background-color: #FFFFFF;
    margin: 20px 0;
    padding: 30px 10px;
    display: flex;

    .info {
     width: 60%;

      .title {
        width: 50%;
        text-align: right;
        margin: 10px 0;
        flex-shrink: 0;
      }

      .info-box {
        display: flex;
        margin: 5px 0;
        align-items: center;

        &.referral {
          .text {
            white-space: break-spaces;
          }
        }
      }

      .iconfont {
        font-size: 25px;
        color: #b5b4b4;
      }

      .text {
        margin-left: 20px;
      }

      .referral-title {
        width: 50%;
        text-align: right;
        line-height: 1;
      }
    }

    .chain {
      width: 30%;
    }
  }

  ::v-deep .van-collapse {
    .van-collapse-item__title {
      background-color: unset;
      padding: 10px 16px 10px 5px;
    }

    .van-collapse-item__content {
      padding: 12px 0;
      background-color: unset;
    }
  }
}
</style>
